/**
 * @fileoverview Item schemas for content item validation and type definitions
 */

import { z } from '@hono/zod-openapi';
import { createListResponseSchema } from './common.js';

/**
 * Schema for AI-extracted notable sections from content
 *
 * Note: startOffset and endOffset are now optional. New items use embedded
 * <!--extract:N--> markers in the content for positioning instead.
 * Offsets are kept optional for backwards compatibility with existing data.
 */
export const ExtractSchema = z
  .object({
    text: z.string().openapi({
      description: 'The extracted quote or section text',
      example: 'Lambda now supports up to 10GB of memory...',
    }),
    startOffset: z.number().int().nonnegative().optional().openapi({
      description: 'Character offset where this extract starts (optional, legacy - use markers instead)',
      example: 1234,
    }),
    endOffset: z.number().int().nonnegative().optional().openapi({
      description: 'Character offset where this extract ends (optional, legacy - use markers instead)',
      example: 1345,
    }),
    sentiment: z.enum(['positive', 'neutral', 'negative']).openapi({
      description: 'Sentiment/quality of this extract',
      example: 'positive',
    }),
    scoreImpact: z.number().openapi({
      description: 'How this extract impacts the overall score (positive or negative)',
      example: 0.3,
    }),
    analysis: z.string().openapi({
      description: 'Why this extract is notable',
      example: 'Strong technical insight about memory optimization patterns',
    }),
  })
  .openapi('Extract');

/**
 * Schema for tracking heuristic adjustments applied to scores
 */
export const HeuristicAppliedSchema = z
  .object({
    id: z.string().openapi({
      description: 'Unique identifier for this heuristic (e.g., author:werner-vogels)',
      example: 'author:werner-vogels',
    }),
    type: z.enum(['author', 'domain', 'site', 'custom']).openapi({
      description: 'Type of heuristic',
      example: 'author',
    }),
    adjustment: z.number().openapi({
      description: 'Score adjustment amount',
      example: 0.5,
    }),
    field: z.enum(['quality', 'relevance']).openapi({
      description: 'Which score field was adjusted',
      example: 'relevance',
    }),
    reason: z.string().openapi({
      description: 'Reason for the adjustment',
      example: 'AWS VP/CTO - highly authoritative source',
    }),
  })
  .openapi('HeuristicApplied');

/**
 * Valid status values for Items
 */
export const ITEM_STATUSES = [
  'process',
  'review',
  'ready',
  'assigned',
  'used', // Reappeared after being in prior issue
  'error', // Processing failed
  'archived',
  'duplicate',
  'blocked',
  'missing-content', // Needs full content capture via browser extension
];

/**
 * Statuses that appear in the "review" queue filter
 * These are items requiring human attention
 */
export const REVIEW_STATUSES = ['review', 'used', 'error', 'duplicate', 'missing-content'];

// Base Item schema without defaults (for partial updates)
const BaseItemSchema = z.object({
  hash: z
    .string()
    .regex(/^[0-9a-z]{12}$/)
    .openapi({
      description: 'Unique 12-character base-36 hash of domain+path for the canonical item',
      example: 'a1b2c3d4e5f6',
    }),
  status: z
    .enum([
      'process',
      'review',
      'ready',
      'assigned',
      'used',
      'error',
      'archived',
      'duplicate',
      'blocked',
      'missing-content',
    ])
    .openapi({
      description: 'Current status of the item',
      example: 'process',
    }),
  // Inoreader information
  source: z
    .object({
      id: z.string().openapi({
        description: 'Inoreader source ID',
        example: 'tag:google.com,2005:reader/item/000000001234abcd',
      }),
      crawlTime: z.string().datetime().openapi({
        description: 'ISO 8601 date time string when Inoreader crawled this item',
        example: '2024-01-15T12:00:00.000Z',
      }),
      categories: z.array(z.string()).openapi({
        description: 'Categories/tags from Inoreader',
        example: ['user/1005921515/state/com.google/reading-list'],
      }),
      origin: z
        .object({
          streamId: z.string().openapi({
            description: 'Feed stream ID',
            example: 'feed/https://example.com/rss',
          }),
          title: z.string().openapi({
            description: 'Feed title',
            example: 'Example Blog',
          }),
          htmlUrl: z.string().url().openapi({
            description: 'Feed website URL',
            example: 'https://example.com',
          }),
        })
        .openapi({
          description: 'Information about the feed origin',
        }),
    })
    .openapi({
      description: 'Inoreader-specific metadata',
    }),
  title: z.string().openapi({
    description: 'Title of the item',
    example: 'Understanding Zod Schemas',
  }),
  published: z.string().datetime().openapi({
    description: 'ISO 8601 timestamp when the item was published',
    example: '2024-01-15T09:00:00Z',
  }),
  created: z.string().datetime().openapi({
    description: 'ISO 8601 timestamp when the item was created',
    example: '2025-01-15T10:00:00Z',
  }),
  modified: z.string().datetime().openapi({
    description: 'ISO 8601 timestamp when the item was last modified',
    example: '2025-01-15T10:00:00Z',
  }),
  url: z.string().url().openapi({
    description: 'Normalized URL of the item',
    example: 'https://example.com/posts/understanding-zod',
  }),
  summary: z.string().optional().openapi({
    description: 'Summary or excerpt of the item',
    example: 'Learn how to use Zod for runtime type validation...',
  }),
  authors: z.array(z.string()).openapi({
    description: 'List of item authors',
    example: ['John Doe', 'Jane Smith'],
  }),
  timeToRead: z.number().nullable().openapi({
    description: 'Estimated reading time in minutes',
    example: 5,
  }),
  type: z.enum(['article', 'video', 'audio', 'image', 'social', 'unknown']).openapi({
    description: 'Type of content',
    example: 'article',
  }),
  category: z.string().optional().openapi({
    description: 'Item category',
    example: 'tutorial',
  }),
  notes: z.string().optional().openapi({
    description: 'User notes about the item',
    example: 'Good example for newsletter, covers recent trends',
  }),
  // Content fields
  content: z.string().optional().openapi({
    description: 'The full content/text of the item',
    example: 'This is the full item content...',
  }),
  // Metadata and processing information (no defaults in base)
  meta: z
    .object({
      domain: z.string().openapi({
        description: 'Domain extracted from the URL',
        example: 'example.com',
      }),
      path: z.string().openapi({
        description: 'Path extracted from the URL',
        example: '/posts/understanding-zod',
      }),
      origUrl: z.string().url().openapi({
        description: 'Original URL before normalization',
        example: 'https://example.com/posts/understanding-zod?utm_source=rss',
      }),
      altUrl: z.string().url().openapi({
        description: 'Alternative URL for the item',
        example: 'https://example.com/posts/understanding-zod',
      }),
      loginRequired: z.boolean().openapi({
        description: 'Whether login is required to read the full item',
        example: false,
      }),
      issues: z.array(z.number()).optional().openapi({
        description: 'Array of issue numbers this item has been assigned to',
        example: [334, 335, 336],
      }),
      archiveUntil: z.string().datetime().nullable().optional().openapi({
        description: 'Timestamp until the item is archived',
        example: '2025-01-15T10:00:00Z',
      }),
      primaryItem: z.string().optional().openapi({
        description: 'Hash of the primary/canonical item if this is marked as duplicate',
        example: 'abc123def456',
      }),
      capture: z
        .object({
          status: z.enum(['pending', 'captured', 'failed']).optional().openapi({
            description: 'Content capture status',
            example: 'captured',
          }),
          capturedAt: z.string().datetime().nullable().optional().openapi({
            description: 'ISO 8601 timestamp when content was captured from browser',
            example: '2025-01-15T10:00:00Z',
          }),
          source: z.enum(['extension', 'manual', 'auto']).optional().openapi({
            description: 'How the content was captured',
            example: 'extension',
          }),
          needsCapture: z.boolean().optional().openapi({
            description: 'Flag indicating this item needs full content capture',
            example: true,
          }),
          captureReason: z.string().optional().openapi({
            description: 'Reason content capture is needed',
            example: 'Short content: 150 chars (min: 500)',
          }),
          readability: z
            .object({
              title: z.string().optional().openapi({
                description: 'Title extracted by Readability',
              }),
              byline: z.string().nullable().optional().openapi({
                description: 'Author byline extracted by Readability',
              }),
              siteName: z.string().nullable().optional().openapi({
                description: 'Site name extracted by Readability',
              }),
              excerpt: z.string().nullable().optional().openapi({
                description: 'Short excerpt extracted by Readability',
              }),
              length: z.number().optional().openapi({
                description: 'Content length in characters',
              }),
              lang: z.string().nullable().optional().openapi({
                description: 'Content language',
              }),
              dir: z.string().nullable().optional().openapi({
                description: 'Content direction (ltr/rtl)',
              }),
              publishedTime: z.string().nullable().optional().openapi({
                description: 'Published time extracted by Readability',
              }),
            })
            .optional()
            .openapi({
              description: 'Metadata extracted by Mozilla Readability',
            }),
        })
        .optional()
        .openapi({
          description: 'Content capture metadata for browser-captured articles',
        }),
    })
    .openapi({
      description: 'Metadata and processing information',
    }),
  sync: z
    .object({
      search: z
        .object({
          lastIndexedAt: z.string().datetime().nullable().optional().openapi({
            description: 'ISO 8601 timestamp when the item was last indexed in search (Algolia)',
            example: '2025-01-15T10:30:00Z',
          }),
          error: z.string().nullable().optional().openapi({
            description: 'Error message if search indexing failed, null if no error',
            example: 'Algolia indexing failed: Invalid API key',
          }),
        })
        .optional()
        .openapi({
          description: 'Search indexing sync metadata (Algolia)',
        }),
    })
    .optional()
    .openapi({
      description: 'External service sync metadata',
    }),
  // AI Analysis fields
  ai: z
    .object({
      summary: z.string().optional().openapi({
        description: 'AI-generated summary of the content',
        example:
          'This item explains the fundamentals of serverless computing and its benefits for modern applications.',
      }),
      category: z.string().optional().openapi({
        description: 'AI-determined category for the content',
        example: 'tutorial',
      }),
      keywords: z.array(z.string()).optional().openapi({
        description: 'AI-extracted keywords from the content',
        example: ['serverless', 'aws', 'lambda', 'cloud', 'architecture'],
      }),
      // Quality and Relevance scores (1-5 scale with decimals)
      qualityScore: z.number().min(1).max(5).optional().openapi({
        description:
          'AI-determined quality score (1-5 with decimals): writing quality, technical accuracy, depth, originality',
        example: 3.7,
      }),
      relevanceScore: z.number().min(1).max(5).optional().openapi({
        description:
          'AI-determined relevance score (1-5 with decimals): alignment with newsletter themes (AWS serverless, AI/ML)',
        example: 4.2,
      }),
      // Raw scores before heuristic adjustments
      rawQualityScore: z.number().min(1).max(5).optional().openapi({
        description: 'Quality score before heuristic adjustments',
        example: 3.5,
      }),
      rawRelevanceScore: z.number().min(1).max(5).optional().openapi({
        description: 'Relevance score before heuristic adjustments',
        example: 4.0,
      }),
      // Notable section extracts
      extracts: z.array(ExtractSchema).optional().openapi({
        description: 'Notable sections/quotes extracted by AI with analysis (0-5 items)',
      }),
      // Scoring rationale
      rationale: z.array(z.string()).optional().openapi({
        description: '3-5 bullet points explaining the scoring decision',
        example: [
          'Strong technical depth with concrete code examples',
          'Directly addresses Lambda cold start optimization',
          'Minor accuracy issue in memory calculation formula',
        ],
      }),
      // Heuristics applied to scores
      heuristicsApplied: z.array(HeuristicAppliedSchema).optional().openapi({
        description: 'Heuristic adjustments applied to raw scores',
      }),
      // Model used for analysis
      model: z.string().optional().openapi({
        description: 'AI model used for analysis',
        example: 'claude-sonnet-4-20250514',
      }),
      // Existing metadata fields
      analyzedAt: z.string().datetime().optional().openapi({
        description: 'ISO 8601 timestamp when AI analysis was performed',
        example: '2024-01-15T10:35:00Z',
      }),
      tokensUsed: z.number().int().nonnegative().optional().openapi({
        description: 'Total tokens used for AI analysis (non-negative integer)',
        example: 342,
      }),
      costUsd: z.number().optional().openapi({
        description: 'Cost of AI analysis in USD',
        example: 0.0023,
      }),
      // Generated blurbs from initial AI workflow
      generatedBlurbs: z
        .array(
          z.object({
            text: z.string().openapi({
              description: 'The generated blurb text',
              example: 'Great post by Jane Smith on Lambda cold starts...',
            }),
            tone: z.enum(['positive', 'neutral', 'negative']).openapi({
              description: 'The tone of this blurb',
              example: 'positive',
            }),
            confidence: z.number().min(0).max(1).openapi({
              description: 'Confidence score for this blurb (0-1)',
              example: 0.85,
            }),
            keyPointsUsed: z.array(z.number()).optional().openapi({
              description: 'Indices into ai.extracts that were used for this blurb',
              example: [0, 2],
            }),
            opinionAligned: z.boolean().optional().openapi({
              description: 'Whether this blurb aligns with a matched opinion',
              example: true,
            }),
          })
        )
        .optional()
        .openapi({
          description: 'Blurbs generated during initial AI analysis (6 blurbs with varied tones)',
        }),
      blurbsGeneratedAt: z.string().datetime().optional().openapi({
        description: 'ISO 8601 timestamp when blurbs were generated',
        example: '2024-01-15T10:40:00Z',
      }),
      blurbOpinionMatched: z.string().optional().openapi({
        description: 'ID of the primary opinion that was matched for blurb generation',
        example: 'cold-starts',
      }),
      blurbOpinionSentiment: z
        .enum(['strongly-favor', 'favor', 'neutral', 'oppose', 'strongly-oppose'])
        .optional()
        .openapi({
          description: 'Sentiment of the matched opinion used for tone distribution',
          example: 'favor',
        }),
      // AI Processing state (for tracking in-progress reprocessing)
      processingTaskId: z.string().nullable().optional().openapi({
        description: 'Task ID of the currently running AI processing task',
        example: 'task_abc123xyz',
      }),
      processingStartedAt: z.string().datetime().nullable().optional().openapi({
        description: 'ISO 8601 timestamp when AI processing started',
        example: '2024-01-15T10:30:00Z',
      }),
    })
    .optional()
    .openapi({
      description: 'AI analysis results and metadata',
    }),
});

// Full Item schema with defaults (for creation and storage)
export const ItemSchema = BaseItemSchema.extend({
  meta: BaseItemSchema.shape.meta.extend({
    issues: z.array(z.number()).optional().default([]).openapi({
      description: 'Array of issue numbers this item has been assigned to',
      example: [334, 335, 336],
    }),
  }),
  sync: z
    .object({
      search: z
        .object({
          lastIndexedAt: z.string().datetime().nullable().optional().default(null).openapi({
            description: 'ISO 8601 timestamp when the item was last indexed in search (Algolia)',
            example: '2025-01-15T10:30:00Z',
          }),
          error: z.string().nullable().optional().default(null).openapi({
            description: 'Error message if search indexing failed, null if no error',
            example: 'Algolia indexing failed: Invalid API key',
          }),
        })
        .optional()
        .openapi({
          description: 'Search indexing sync metadata (Algolia)',
        }),
    })
    .optional()
    .openapi({
      description: 'External service sync metadata',
    }),
}).openapi('Item');

// Create schema - omit auto-generated fields (hash is provided during creation)
export const CreateItemSchema = ItemSchema.omit({
  created: true,
  modified: true,
}).openapi('CreateItem');

// Update schema - partial from BASE to avoid applying defaults
// Omitted fields (hash, created, modified) are stripped, not rejected
export const UpdateItemSchema = BaseItemSchema.partial()
  .omit({
    hash: true, // Immutable
    created: true, // Immutable
    modified: true, // Auto-managed
  })
  .openapi('UpdateItem');

export const ItemListResponseSchema = createListResponseSchema(
  ItemSchema,
  'items',
  'ItemListResponse'
);

/**
 * JSDoc type definition for Item - Content item type
 * @typedef {Object} ItemType
 * @property {string} hash - Unique 12-character base-36 hash of domain+path
 * @property {'process'|'review'|'ready'|'assigned'|'used'|'error'|'archived'|'duplicate'|'blocked'|'missing-content'} status - Current status
 * @property {Object} source - Inoreader-specific metadata
 * @property {string} title - Title of the item
 * @property {string} published - ISO 8601 timestamp when published
 * @property {string} created - ISO 8601 timestamp when created
 * @property {string} modified - ISO 8601 timestamp when last modified
 * @property {string} url - Normalized URL of the item
 * @property {string} [summary] - Summary or excerpt
 * @property {string[]} authors - List of item authors
 * @property {number|null} timeToRead - Estimated reading time in minutes
 * @property {'article'|'video'|'audio'|'image'|'social'|'unknown'} type - Type of content
 * @property {string} [category] - Item category
 * @property {string} [notes] - User notes
 * @property {string} [content] - Full content/text
 * @property {Object} meta - Metadata and processing information
 * @property {Object} [sync] - External service sync metadata
 * @property {Object} [ai] - AI analysis results
 */

export const ItemType = /** @type {ItemType} */ ({});
